import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../components/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/Form";
import { Input } from "../../components/ui/Input";
import { X, Loader2, Upload, User } from "lucide-react";
import {
  useAdminFetchAvailableDevicesQuery,
  useAdminCreateCustomerMutation,
  useAdminUpdateCustomerMutation,
  AdminFetchCustomersDocument,
} from "../../generated/graphql";
import { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { ProtectedRoutes } from "../../types/Routes";
import toast from "react-hot-toast";
import { PopUp } from "../atoms/PopUp";
import { uploadImage } from "../../utils/cloudinary";

const formSchema = z.object({
  address: z.string().min(2, "Address is required"),
  avatar: z.string().optional(),
  country: z.string().min(2, "Country is required"),
  devices: z.array(z.string().min(2)),
  email: z.string().email("Invalid email address"),
  fullName: z.string().min(2, "Full name is required"),
  gender: z.string().min(2, "Gender is required"),
  phone: z.string().min(2, "Phone number is required"),
  wallet: z.string().optional(),
});

interface DeviceOption {
  value: string;
  label: string;
  device: any;
}

interface AddCustomerFormProps {
  isEditMode?: boolean;
  initialData?: any; // Changed from Customer to any to handle transformed data
  customerId?: string;
}

const AddCustomerForm = ({
  isEditMode = false,
  initialData,
  customerId,
}: AddCustomerFormProps) => {
  const [selectedDevices, setSelectedDevices] = useState<DeviceOption[]>([]);
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const navigate = useNavigate();

  // Fetch available devices
  const { data: devicesData, loading: devicesLoading } =
    useAdminFetchAvailableDevicesQuery({
      variables: {
        params: {
          pagination: {
            page: 1,
            take: 100,
          },
          filter: {
            field: "status",
            value: "available",
          },
        },
      },
    });

  const devices = devicesData?.data?.payload || [];

  // Transform devices to react-select options
  const deviceOptions: DeviceOption[] = devices.map((device: any) => ({
    value: device._id,
    label: device.serialNumber,
    device: device,
  }));

  // Handle edit mode initialization
  useEffect(() => {
    if (isEditMode && initialData) {
      setSelectedDevices(initialData.devices || []);
      setAvatarPreview(initialData.avatar || "");
    }
  }, [isEditMode, initialData]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: initialData?.address || "",
      avatar: initialData?.avatar || "",
      country: initialData?.country || "",
      devices: initialData?.devices?.map((device: any) => device.value) || [],
      email: initialData?.email || "",
      fullName: initialData?.fullName || "",
      gender: initialData?.gender || "",
      phone: initialData?.phone || "",
      wallet: initialData?.wallet || "",
    },
  });

  // Create customer mutation
  const [createCustomer, { loading: isCreating }] =
    useAdminCreateCustomerMutation({
      onCompleted: data => {
        if (data.data.alert.type === "success") {
          // Navigate back to customers list
          navigate(ProtectedRoutes.CUSTOMERS);
        }
      },
      refetchQueries: [
        {
          query: AdminFetchCustomersDocument,
          variables: {
            params: {
              pagination: {
                page: 1,
                take: 10,
              },
            },
          },
        },
      ],
    });

  // Update customer mutation
  const [updateCustomer, { loading: isUpdating }] =
    useAdminUpdateCustomerMutation({
      onCompleted: data => {
        if (data.data.alert.type === "success") {
          // Navigate back to customers list
          navigate(ProtectedRoutes.CUSTOMERS);
        }
      },
      refetchQueries: [
        {
          query: AdminFetchCustomersDocument,
          variables: {
            params: {
              pagination: {
                page: 1,
                take: 10,
              },
            },
          },
        },
      ],
    });

  const handleDeviceChange = (selectedOptions: readonly DeviceOption[]) => {
    const selected = selectedOptions as DeviceOption[];
    setSelectedDevices(selected);
    form.setValue(
      "devices",
      selected.map(option => option.value)
    );
  };

  // Handle avatar upload
  const handleAvatarUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.custom(t => (
        <PopUp t={t} message="Please select a valid image file" type="error" />
      ));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.custom(t => (
        <PopUp
          t={t}
          message="Image size should be less than 5MB"
          type="error"
        />
      ));
      return;
    }

    setIsUploadingAvatar(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to Cloudinary
      const uploadResult = await uploadImage(file);

      // Update form with Cloudinary URL
      form.setValue("avatar", uploadResult.secure_url);

      toast.custom(t => (
        <PopUp t={t} message="Avatar uploaded successfully!" type="success" />
      ));
    } catch (error) {
      console.error("Avatar upload error:", error);
      toast.custom(t => (
        <PopUp
          t={t}
          message="Failed to upload avatar. Please try again."
          type="error"
        />
      ));
      setAvatarPreview("");
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Clear avatar
  const handleClearAvatar = () => {
    setAvatarPreview("");
    form.setValue("avatar", "");
  };

  // const handleDeviceRemove = (deviceId: string) => {
  //   const newSelectedDevices = selectedDevices.filter(
  //     option => option.value !== deviceId
  //   );
  //   setSelectedDevices(newSelectedDevices);
  //   form.setValue(
  //     "devices",
  //     newSelectedDevices.map(option => option.value)
  //   );
  // };

  // Custom option component for react-select
  const customOption = ({ data, ...props }: any) => {
    return (
      <div
        {...props.innerProps}
        className="flex items-center gap-3 p-3 transition-colors duration-150 cursor-pointer"
      >
        {data.device.deviceModel?.brand?.brandLogo && (
          <div className="flex-shrink-0">
            <img
              src={data.device.deviceModel.brand.brandLogo}
              alt={data.device.deviceModel.brand.brandName || "Brand"}
              className="object-contain w-6 h-6 rounded"
            />
          </div>
        )}
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {data.device.serialNumber}
          </span>
          <span className="text-xs text-gray-500 truncate">
            {data.device.deviceModel?.brand?.brandName || "Unknown Brand"} -{" "}
            {data.device.deviceModel?.modelName || "Unknown Model"}
          </span>
        </div>
      </div>
    );
  };

  // Custom value component for react-select
  const customValue = ({ data, removeProps }: any) => {
    const isPaid = data.device.amountPaid && data.device.amountPaid > 0;

    return (
      <div className="flex items-center gap-1 px-2 py-1 mr-1 text-xs font-medium text-white border rounded-md shadow-sm border-main bg-main">
        <span className="truncate max-w-20">{data.device.serialNumber}</span>
        {!isPaid && (
          <button
            type="button"
            onClick={removeProps.onClick}
            className="flex-shrink-0 ml-1 p-0.5 hover:bg-white/20 hover:text-white rounded transition-colors duration-150"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      if (isEditMode && customerId) {
        // Update existing customer
        await updateCustomer({
          variables: {
            customerId: customerId,
            updateCustomerInput: {
              address: values.address,
              avatar: values.avatar || "",
              country: values.country,
              devices: values.devices,
              email: values.email,
              fullName: values.fullName,
              gender: values.gender,
              phone: values.phone,
              wallet: values.wallet || "",
            },
          },
        });
      } else {
        // Create new customer
        await createCustomer({
          variables: {
            createCustomerInput: {
              address: values.address,
              avatar: values.avatar || "",
              country: values.country,
              devices: values.devices,
              email: values.email,
              fullName: values.fullName,
              gender: values.gender,
              phone: values.phone,
              wallet: values.wallet || "",
            },
          },
        });
      }
    } catch (error) {
      console.error("Error saving customer:", error);
    }
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Customer Information</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {/* Avatar Upload Section */}
              <FormField
                control={form.control}
                name="avatar"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Profile Picture (Optional)</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-4">
                        {/* Avatar Preview */}
                        <div className="flex-shrink-0">
                          {avatarPreview ? (
                            <div className="relative">
                              <img
                                src={avatarPreview}
                                alt="Avatar preview"
                                className="object-cover w-20 h-20 border-2 border-gray-200 rounded-full"
                              />
                              <button
                                type="button"
                                onClick={handleClearAvatar}
                                className="absolute flex items-center justify-center w-6 h-6 text-xs text-white transition-colors bg-red-500 rounded-full -top-2 -right-2 hover:bg-red-600"
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center w-20 h-20 bg-gray-100 border-2 border-gray-300 border-dashed rounded-full">
                              <User className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Upload Area */}
                        <div className="flex-1">
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleAvatarUpload(file);
                                }
                              }}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              disabled={isUploadingAvatar}
                            />
                            <div
                              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                                isUploadingAvatar
                                  ? "border-gray-300 bg-gray-50 opacity-50"
                                  : "border-gray-300 hover:border-gray-400 cursor-pointer"
                              }`}
                            >
                              {isUploadingAvatar ? (
                                <div className="flex flex-col items-center">
                                  <Loader2 className="w-6 h-6 mb-2 text-gray-400 animate-spin" />
                                  <p className="text-sm text-gray-500">
                                    Uploading...
                                  </p>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center">
                                  <Upload className="w-6 h-6 mb-2 text-gray-400" />
                                  <p className="text-sm font-medium text-gray-600">
                                    Click to upload or drag and drop
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    PNG, JPG up to 5MB
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Full Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <FormControl>
                      <Input placeholder="Gender" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input placeholder="Address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input placeholder="Country" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="devices"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Devices</FormLabel>
                    <FormControl>
                      <Select
                        isMulti
                        options={deviceOptions}
                        value={selectedDevices}
                        onChange={handleDeviceChange}
                        isLoading={devicesLoading}
                        placeholder={
                          devicesLoading
                            ? "Loading devices..."
                            : "Select devices"
                        }
                        components={{
                          Option: customOption,
                          MultiValue: customValue,
                        }}
                        styles={{
                          // Main input container - the visible select box
                          control: (provided: any) => ({
                            ...provided,
                            border: "1px solid #e5e7eb",
                            borderRadius: "0.375rem",
                            minHeight: "30px",
                            backgroundColor: "#ffffff",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "none",
                            },
                            "&:focus-within": {
                              borderColor: "#e5e7eb",
                              boxShadow: "0 0 0 0px none",
                            },
                          }),
                          // Remove button (X) inside selected value badges
                          multiValueRemove: (provided: any) => ({
                            ...provided,
                            color: "#1e40af",
                            "&:hover": {
                              backgroundColor: "#bfdbfe",
                              color: "#ffa300",
                            },
                          }),
                          // Placeholder text when no devices are selected
                          placeholder: (provided: any) => ({
                            ...provided,
                            color: "#9ca3af",
                            fontSize: "0.9rem",
                          }),
                          // Text input field for searching
                          input: (provided: any) => ({
                            ...provided,
                            color: "#374151",
                          }),
                        }}
                        filterOption={(option: any, inputValue: string) => {
                          const device = option.data.device;
                          const searchLower = inputValue.toLowerCase();
                          return (
                            device.serialNumber
                              ?.toLowerCase()
                              .includes(searchLower) ||
                            device.deviceModel?.modelName
                              ?.toLowerCase()
                              .includes(searchLower) ||
                            device.deviceModel?.brand?.brandName
                              ?.toLowerCase()
                              .includes(searchLower)
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="wallet"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>USDC Wallet (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter USDC wallet address "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center justify-end">
              <Button
                type="submit"
                disabled={isCreating || isUpdating || isUploadingAvatar}
                className="text-white bg-main hover:bg-main/90"
              >
                {isCreating || isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode
                      ? "Updating Customer..."
                      : "Creating Customer..."}
                  </>
                ) : isEditMode ? (
                  "Update Customer"
                ) : (
                  "Create Customer"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default AddCustomerForm;
